import React, { useState, useEffect } from 'react';
import { Globe, Check, Info, CirclePlus, Trash2, CheckCircle2, Loader2 } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { fetchWebScrape, fetchWebsiteDocumentCache, cancelWebScrape } from '../../services/api/virtualAssistant';
import { WebsiteData, LinkType } from "../../types/CourseData";
import { v4 as uuidv4 } from 'uuid';
import { getSafeImageSrc } from '../../utils/imageUtils';

interface WebsiteConnectTabProps {
  selectedCourseData: WebsiteData;
  websites: WebsiteData[];
  setWebsites: React.Dispatch<React.SetStateAction<WebsiteData[]>>;
  assistant: any;
  systemConfig: { Id: string; ConfigValue: string; }[];
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
  triggerAutoSave: () => void;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
}

const WebsiteConnectTab: React.FC<WebsiteConnectTabProps> = ({
  selectedCourseData,
  websites,
  setWebsites,
  assistant,
  systemConfig,
  setCustomModal,
  triggerAutoSave,
  setPendingSave
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [safeAiIconSrc, setSafeAiIconSrc] = useState<string>(assistant?.app_ai_face);

  // Update safe source when app_ai_face changes
  useEffect(() => {
    const updateSafeSource = async (): Promise<void> => {
      const src = assistant?.app_ai_face;
      const safeSrc = await getSafeImageSrc(src);
      setSafeAiIconSrc(safeSrc);
    };
    updateSafeSource();
  }, [assistant?.app_ai_face]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const handleUrlChange = (websiteId: number, newUrl: string) => {
    setWebsites((prevData) =>
      prevData.map((website) =>
        website.id === websiteId ? { ...website, url: newUrl, connectionState: 'idle' } : website
      )
    );
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>, courseId: number) => {
    const updatedWebsiteName = event.target.value;
    setWebsites(prevWebsites => 
      prevWebsites.map(website =>
        website.id === courseId 
          ? { ...website, website_name: updatedWebsiteName } 
          : website
      )
    );
  };

  const handleExternalLinksToggle = () => {
    if (selectedCourseData) {
      setWebsites(prevData => prevData.map(website => {
        if (website.id === selectedCourseData.id) {
          const next = !website.includeExternalLinks;
          return {
            ...website,
            includeExternalLinks: next,
            restrictToStartPath: next ? false : website.restrictToStartPath,
          };
        }
        return website;
      }));
      if (selectedCourseData.connectionState === 'connected') {
        triggerAutoSave();
        setPendingSave(true);
      }
    }
  };

  const handleRestrictToStartPathToggle = () => {
    if (selectedCourseData) {
      setWebsites(prevData => prevData.map(website => {
        if (website.id === selectedCourseData.id) {
          const next = !website.restrictToStartPath;
          return {
            ...website,
            restrictToStartPath: next,
            includeExternalLinks: next ? false : website.includeExternalLinks,
          };
        }
        return website;
      }));
      if (selectedCourseData.connectionState === 'connected') {
        triggerAutoSave();
        setPendingSave(true);
      }
    }
  };

  const handleFollowLinkLevelChange = (newLevel: string) => {
    if (selectedCourseData) {
      setWebsites(prevWebsites =>
        prevWebsites.map(website =>
          website.id === selectedCourseData.id
            ? { ...website, followLinkLevel: newLevel, connectionState: 'idle' }
            : website
        )
      );
      if (selectedCourseData.connectionState === 'connected') {
        triggerAutoSave();
        setPendingSave(true);
      }
    }
  };

  const updateExcludePattern = (index: number, value: string) => {
    if (selectedCourseData) {
      setWebsites(prevData => prevData.map(website => {
        if (website.id === selectedCourseData.id) {
          const newPatterns = [...website.excludePatterns];
          newPatterns[index] = value;
          return {
            ...website,
            excludePatterns: newPatterns,
            connectionState: 'idle'
          };
        }
        return website;
      }));
      if (selectedCourseData.connectionState === 'connected') {
        triggerAutoSave();
        setPendingSave(true);
      }
    }
  };

  const addExcludePattern = () => {
    if (selectedCourseData) {
      setWebsites(prevData => prevData.map(website => {
        if (website.id === selectedCourseData.id) {
          return {
            ...website,
            excludePatterns: [...website.excludePatterns, ''],
          };
        }
        return website;
      }));
      if (selectedCourseData.connectionState === 'connected') {
        triggerAutoSave();
        setPendingSave(true);
      }
    }
  };

  const removeExcludePattern = (index: number) => {
    setWebsites(prevWebsites =>
      prevWebsites.map(website => {
        if (website.id === selectedCourseData.id) {
          const updatedPatterns = (website.excludePatterns ?? []).filter((_, idx) => idx !== index);
          return {
            ...website,
            excludePatterns: updatedPatterns,
            connectionState: 'idle'
          };
        }
        return website;
      })
    );
    if (selectedCourseData.connectionState === 'connected') {
      triggerAutoSave();
      setPendingSave(true);
    }
  };

  const setActiveTab = (courseId: number, tab: 'CONNECT' | 'PREVIEW') => {
    setWebsites(prev => prev.map(course => 
      course.id === courseId ? { ...course, activeTab: tab } : course
    ));
  };

  const handleCancelCrawl = (websiteId: number) => {
    const warningMessage = 'This action will cancel the crawling process. Are you sure?';
    
    setCustomModal({
      isOpen: true,
      title: "Cancel Confirmation",
      message: warningMessage,
      type: "confirmation",
      onConfirm: async () => {
        setCustomModal(prev => ({ ...prev, isOpen: false }));

        setCustomModal({
          isOpen: true,
          title: "Cancelling...",
          message: "Stopping the crawling process...",
          type: "loading",
        });

        const website = websites.find(w => w.id === websiteId);
        const documentId = website?.document_id;

        if (documentId) {
          try {
            await cancelWebScrape(documentId);
            setCustomModal({
              isOpen: true,
              title: "Cancelled Successfully",
              message: "The crawling process has been cancelled.",
              type: "success",
            });
          } catch (error: any) {
            setCustomModal({
              isOpen: true,
              title: "Failed to Cancel",
              message: `Unable to stop the crawling process: ${error.message || 'Unknown error'}`,
              type: "error",
            });
          }
        } else {
          setCustomModal({
            isOpen: true,
            title: "Cancelled",
            message: "Local crawling process has been stopped.",
            type: "success",
          });
        }

        // Update local state
        setWebsites(prev =>
          prev.map(c =>
            c.id === websiteId
              ? {
                  ...c,
                  connectionState: 'idle',
                  status: 'idle'
                }
              : c
          )
        );

        setPendingSave(false);
        triggerAutoSave();
      }
    });
  };

  const handleConnect = async (courseId: number, source: 'click' | 'loop' | 'refresh') => {
    if (!assistant?.app_id) {
      setWebsites((prev) =>
        prev.map((c) =>
          c.id === courseId
            ? { 
                ...c, 
                UrlError: 'Chatbot must be configured and saved before connecting websites. Please complete your chatbot setup first.' 
              }
            : c
        )
      );
      
      setCustomModal({
        isOpen: true,
        title: "Configuration Required",
        message: "Your chatbot needs to be properly configured and saved before you can connect websites. Please complete the chatbot setup first.",
        type: "warning",
      });
      return;
    }

    const course = websites.find(c => c.id === courseId);
    let max_depth: string = '2';

    if (course?.followLinkLevel !== "All" && course?.followLinkLevel !== undefined) {
      max_depth = course.followLinkLevel;
    }
    
    if (!course?.url) {
      setWebsites((prev) =>
        prev.map((c) =>
          c.id === courseId
            ? { ...c, UrlError: 'Please enter a valid URL.' }
            : c
        )
      );
      return;
    }

    setWebsites((prev) =>
      prev.map((c) =>
        c.id === courseId ? { ...c, UrlError: undefined } : c
      )
    );
      
    if (course?.connectionState !== 'connected') {
      setWebsites(prev => prev.map(course => 
        course.id === courseId 
          ? { ...course, connectionState: 'connecting', status: 'connecting' } 
          : course
      ));
    }

    try {
      const blockedPatternsString = selectedCourseData?.excludePatterns?.join(",") || "";
      const include_external_links = selectedCourseData?.includeExternalLinks || false;
      const restrict_to_start_path = selectedCourseData?.restrictToStartPath || false;

      const result = await fetchWebScrape(course.url, Number(max_depth), blockedPatternsString, include_external_links, restrict_to_start_path, assistant.app_id);
      
      if (result.document_id) {
        setWebsites(prev => prev.map(c =>
          c.id === courseId
            ? {
                ...c,
                connectionState: 'connected',
                status: 'connecting',
                websites_links: [],
                website_id: uuidv4(),
                document_id: result.document_id,
                activeTab: 'PREVIEW'
              }
            : c
        ));
      }
    } catch (error: any) {
      if (error instanceof Response) {
        const errorData = await error.json();
        setWebsites(prev => prev.map((course) =>
          course.id === courseId
            ? { 
                ...course, 
                connectionState: 'idle', 
                UrlError: errorData.detail || 'Failed to connect. Please try again.'
              }
            : course
        ));
      }
    }
  };

  return (
    <div className="grid grid-cols-5 gap-6 p-4 border border-gray-300 rounded-md shadow-lg">
      {/* Left Column: Form Fields */}
      <div className="col-span-3">
        <div className="space-y-4">
          <div className="flex flex-1 pt-2">
            <label className="form-label mt-4">Name :</label>
            <input
              type="text"
              value={selectedCourseData.website_name}  
              onChange={(e) => handleNameChange(e, selectedCourseData.id)}
              className="mt-3 ml-4 w-[78%] p-2 border border-gray-300 rounded-lg shadow-sm focus:ring-0 focus:border-gray-500 focus:outline-none sm:text-sm"
              disabled={selectedCourseData?.connectionState === 'connecting'}
            />
          </div>
          
          <div className="flex flex-1">
            <label className="form-label mt-4">URL:</label>
            <input
              type="text"
              value={selectedCourseData?.url || ''}
              onChange={(e) => handleUrlChange(selectedCourseData?.id, e.target.value)}
              placeholder="Eg: https://www.kellogg.northwestern.edu/"
              className="mt-3 ml-8 w-[78%] p-2 border border-gray-300 rounded-lg shadow-sm focus:ring-0 focus:border-gray-500 focus:outline-none sm:text-sm"
              disabled={selectedCourseData?.connectionState === 'connecting'}
            />
          </div>
          
          {selectedCourseData?.UrlError && (
            <p className="text-red-500 text-sm ml-[12%]">{selectedCourseData.UrlError}</p>
          )}

          {/* Advanced Settings Accordion */}
          <div className="w-full max-w-3xl mx-auto mt-6">
            <div className="border border-gray-300 rounded-md mb-2">
              <div className="bg-gray-100" id="headingOne">
                <h5 className="mb-0">
                  <button
                    className="w-full text-left py-3 px-4 font-medium text-gray-400 hover:text-gray-700 focus:outline-none"
                    onClick={toggleAccordion}
                    aria-expanded={isOpen ? 'true' : 'false'}
                    aria-controls="collapseOne"
                  >
                    Advanced Settings
                  </button>
                </h5>
              </div>

              <div
                id="collapseOne"
                className={`transition-all duration-500 overflow-hidden ${isOpen ? 'max-h-[500px] py-4' : 'max-h-0 py-0'}`}
                aria-labelledby="headingOne"
              >
                <div className="p-4 space-y-6">
                  {/* Crawl Depth Dropdown */}
                  <div className="flex items-center space-x-4">
                    <label className="form-label text-sm font-medium flex items-center space-x-2">
                      <span>Crawl Depth (Levels)</span>
                      <div className="relative group">
                        <Info className="h-4 w-4 text-gray-500 cursor-pointer" />
                        <div className="absolute ml-5 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-96">
                          <p><strong>How deep the crawler should follow internal links from the starting page</strong></p>
                        </div>
                      </div>
                      <span>:</span>
                    </label>
                    <select
                      value={selectedCourseData?.followLinkLevel}
                      onChange={(e) => handleFollowLinkLevelChange(e.target.value)}
                      className="px-3 py-2 w-[20%] border border-gray-300 rounded-lg shadow-sm focus:ring-0 focus:border-gray-500 focus:outline-none"
                    >
                      <option value="5">5</option>
                      <option value="4">4</option>
                      <option value="3">3</option>
                      <option value="2">2</option>
                      <option value="1">1</option>
                      <option value="0">0</option>
                    </select>
                  </div>

                  {/* External Links Toggle */}
                  {selectedCourseData?.followLinkLevel !== '0' && (
                    <div className="flex items-center space-x-4">
                      <label className="form-label text-sm font-medium flex items-center space-x-1">
                        <span>Include External Links</span>
                        <div className="relative group">
                          <Info className="h-4 w-4 text-gray-500 cursor-pointer ml-1" />
                          <div className="absolute bottom-full mb-1 ml-2 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-64">
                           <p><strong>When enabled, the crawler will follow links that lead to external domains outside the main website</strong></p>
                          </div>
                        </div>
                        <span>:</span>
                      </label>
                      <div className="flex justify-end items-center">
                        <div
                          role="checkbox"
                          aria-label="Include External Links"
                          aria-checked={!!selectedCourseData?.includeExternalLinks}
                          aria-disabled={!!selectedCourseData?.restrictToStartPath}
                          data-testid="include-external-links-toggle"
                          className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center
                            ${selectedCourseData?.includeExternalLinks ? 'bg-black' : 'bg-white'}
                            ${selectedCourseData?.restrictToStartPath ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={selectedCourseData?.restrictToStartPath ? undefined : handleExternalLinksToggle}
                        >
                          {selectedCourseData?.includeExternalLinks && <Check className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Restrict to Start URL Path Toggle */}
                  {selectedCourseData?.followLinkLevel !== '0' && (
                    <div className="flex items-center space-x-4">
                      <label className="form-label text-sm font-medium flex items-center space-x-1">
                        <span>Restrict to start URL path</span>
                        <div className="relative group">
                          <Info className="h-4 w-4 text-gray-500 cursor-pointer ml-1" />
                          <div className="absolute bottom-full mb-1 ml-2 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-80">
                           <p><strong>Only crawl pages under the start URL's path. For example, if the start URL is example.com/docs, pages like example.com/docs/api are crawled but example.com/blog is skipped.</strong></p>
                          </div>
                        </div>
                        <span>:</span>
                      </label>
                      <div className="flex justify-end items-center">
                        <div
                          role="checkbox"
                          aria-label="Restrict to start URL path"
                          aria-checked={!!selectedCourseData?.restrictToStartPath}
                          aria-disabled={!!selectedCourseData?.includeExternalLinks}
                          data-testid="restrict-to-start-path-toggle"
                          className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center
                            ${selectedCourseData?.restrictToStartPath ? 'bg-black' : 'bg-white'}
                            ${selectedCourseData?.includeExternalLinks ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          onClick={selectedCourseData?.includeExternalLinks ? undefined : handleRestrictToStartPathToggle}
                        >
                          {selectedCourseData?.restrictToStartPath && <Check className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Exclude Patterns */}
                  {selectedCourseData?.followLinkLevel !== '0' && (
                    <div>
                      <label className="form-label text-sm font-medium flex items-center space-x-1">
                        <span>Exclude URL Patterns</span>
                        <div className="relative group">
                          <Info className="h-4 w-4 text-gray-500 cursor-pointer ml-1" />
                          <div className="absolute bottom-full mb-1 ml-2 hidden group-hover:block bg-white border border-gray-300 rounded-lg shadow-lg p-2 text-left text-sm text-gray-700 w-80">
                            <p><strong>Provides URL patterns (e.g., /blog, /logout) that the crawler should skip while crawling</strong></p>
                          </div>
                        </div>
                        <span>:</span>
                      </label>
                      {selectedCourseData && selectedCourseData.excludePatterns.map((pattern, index) => (
                        <div key={index} className="flex space-x-2 mt-2">
                          <input
                            type="text"
                            value={pattern}
                            onChange={(e) => updateExcludePattern(index, e.target.value)}
                            className="w-full ml-2 py-2 newassistant-input"
                          />
                          <button
                            onClick={() => removeExcludePattern(index)}
                            className="px-3 py-2 rounded-md"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div onClick={addExcludePattern} className="mt-4 flex ml-[95%]">
                        <CirclePlus className="h-6 w-6 text-gray-500" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Column: Connection Status */}
      <div className="flex col-span-2 flex-col items-center mt-[10%] space-y-4 h-full">
        <div className="flex items-center space-x-4">
          {/* AI Icon */}
          {assistant?.app_ai_face ? (
            <img
              src={safeAiIconSrc}
              alt="AI Face"
              className="h-8 w-8 cursor-pointer"
            />
          ) : (
            <FontAwesomeIcon icon={faRobot} className="h-8 w-8 text-black" />
          )}
          
          {/* Connecting Animation */}
          <div className="flex items-center space-x-2">
            {[...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className={`h-1 w-1 rounded-full ${
                  selectedCourseData?.connectionState === 'connecting'
                    ? 'bg-black animate-pulse'
                    : 'bg-black'
                }`}
                style={
                  selectedCourseData?.connectionState === 'connecting'
                    ? { animationDelay: `${idx * 150}ms` }
                    : {}
                }
              />
            ))}
          </div>
          
          {/* Website Icon */}
          <Globe className="h-8 w-8 text-black" />
        </div>

        {/* Connection Buttons */}
        {selectedCourseData?.connectionState === 'idle' && (
          <button 
            onClick={() => handleConnect(selectedCourseData.id, 'click')}
            className="bg-black text-white hover:text-white hover:border-b-4 hover:bg-black py-2 px-6 border rounded-full whitespace-nowrap"
          >
            CONNECT
          </button>
        )}
                
        {selectedCourseData?.connectionState === 'connecting' && (
          <button 
            className="pl-10 bg-blue-600 text-white py-2 px-8 rounded-md flex items-center space-x-4 cursor-not-allowed"
            disabled
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Connecting...</span>
          </button>
        )}
              
        {selectedCourseData?.connectionState === 'connected' && (
          <button 
            className="bg-green-600 text-white py-2 px-6 rounded-md flex items-center space-x-2 cursor-not-allowed"
            disabled
          >
            <CheckCircle2 className="h-4 w-4" />
            <span>Connected</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default WebsiteConnectTab;